import {useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import {ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import AtomImage, {FETCHPRIORITY} from '@s-ui/react-atom-image'
import Context from '@s-ui/react-context'
import ActionsShareUrl, {MODAL_SHARE_CONTEXTS, shareUrlButtonTypes} from '@adv-mt/mt-actions-share-url'
import {useDateFormat} from '@adv-mt/mt-hook-use-date-format'
import IconComments from '@adv-mt/mt-svg-icons/lib/Comments'
import TagChip, {tagChipSizes} from '@adv-mt/mt-tag-chip'
import TitleBasic, {titleBasicColors, titleBasicTags} from '@adv-mt/mt-title-basic'
import UserAvatar from '@adv-mt/mt-user-avatar'

const linkFactoryDefaultProp = ({children, ...rest}) => <a {...rest}>{children}</a>

const BASE_CLASS = 'mt-PanelNewsDetailHeader'
const MEDIA_CLASS = `${BASE_CLASS}-media`
const MEDIA_SRC_CLASS = `${MEDIA_CLASS}Src`
const TAGS_CLASS = `${BASE_CLASS}-tags`
const TITLE_CLASS = `${BASE_CLASS}-title`
const INFO_CLASS = `${BASE_CLASS}-info`
const INFO_AUTHOR_CLASS = `${INFO_CLASS}Author`
const AUTHOR_TEXT_CLASS = `${INFO_AUTHOR_CLASS}Text`
const AUTHOR_NAME_CLASS = `${INFO_AUTHOR_CLASS}Name`
const DATE_CLASS = `${INFO_CLASS}Date`
const ACTIONS_CLASS = `${INFO_CLASS}Actions`
const COMMENTS_CLASS = `${INFO_CLASS}Comments`
const SHARE_CLASS = `${INFO_CLASS}Share`
const ACTION_SLOT_CLASS = `${INFO_CLASS}ActionSlot`

const TITLE_SIZE = 'l'

const PanelNewsDetailHeader = ({
  actionSlot,
  author,
  category,
  date,
  linkFactory = linkFactoryDefaultProp,
  mainImageSrc,
  showNumberOfComments,
  slug,
  tag,
  title,
  totalNumberOfComments
}) => {
  const {domain} = useContext(Context)
  const config = domain.get('config')
  const urlStatics = config.get('urlStatics')
  const defaultImagePlaceholder = config.get('DEFAULT_IMAGE_PLACEHOLDER')
  const defaultImagePlaceholderUrl = `${urlStatics}${defaultImagePlaceholder}`

  const {descriptiveShort: parsedDate} = useDateFormat(date)
  const isWebView = config.get('isWebview')

  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const urlToShare = isMounted && window.location.href

  const {
    TRACKING_EVENT_NAMES: {NEWS_SHARED_CLICKED, NEWS_SHARED}
  } = config.get('tracking')

  return (
    <div className={BASE_CLASS}>
      <div className={MEDIA_CLASS}>
        <div className={MEDIA_SRC_CLASS}>
          <AtomImage src={mainImageSrc || defaultImagePlaceholderUrl} alt={title} fetchPriority={FETCHPRIORITY.HIGH} />
        </div>
      </div>
      <div className={TAGS_CLASS}>
        {category && (
          <TagChip
            label={tag.text}
            type={category}
            href={tag.url}
            size={tagChipSizes.MEDIUM}
            linkFactory={linkFactory}
            isFitted
          />
        )}
      </div>
      {title ? (
        <div className={TITLE_CLASS}>
          <TitleBasic as={titleBasicTags.h1} color={titleBasicColors.black} size={TITLE_SIZE} title={title} />
        </div>
      ) : null}
      <div className={INFO_CLASS}>
        <div className={INFO_AUTHOR_CLASS}>
          {author ? <UserAvatar imageSrc={author.image} /> : null}
          <div className={AUTHOR_TEXT_CLASS}>
            {author && <span className={AUTHOR_NAME_CLASS}>{author.label}</span>}
            <time className={DATE_CLASS} dateTime={date} data-testid="publicationDate">
              {parsedDate}
            </time>
          </div>
        </div>
        {!isWebView && (
          <div className={ACTIONS_CLASS}>
            {showNumberOfComments && (
              <Comments slug={slug} totalNumberOfComments={totalNumberOfComments} linkFactory={linkFactory} />
            )}
            {actionSlot && <div className={ACTION_SLOT_CLASS}>{actionSlot}</div>}
            <div className={SHARE_CLASS}>
              <ActionsShareUrl
                url={urlToShare}
                type={shareUrlButtonTypes.INLINE_BUTTON}
                trackingSharedEventName={NEWS_SHARED}
                trackingClickEventName={NEWS_SHARED_CLICKED}
                shareText={title}
                shareContext={MODAL_SHARE_CONTEXTS.NEWS}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const CommentsContent = ({count}) => {
  return (
    <>
      <IconComments size={ATOM_ICON_SIZES.medium} /> {count}
    </>
  )
}
CommentsContent.propTypes = {
  count: PropTypes.number
}

const Comments = ({linkFactory, slug, totalNumberOfComments}) => {
  const {domain} = useContext(Context)
  const domainConfig = domain.get('config')
  const {NEWS_COMMENTS_BASE_PATH} = domainConfig.get('page')
  const {MIN_COMMENTS_TO_LINK_REVIEWS_PAGE} = domainConfig.get('COMMON')

  const shouldRenderLink = totalNumberOfComments >= MIN_COMMENTS_TO_LINK_REVIEWS_PAGE
  const Link = linkFactory
  const content = <CommentsContent count={totalNumberOfComments} />
  const handleClick = () => {
    const {ARTICLE_COMMENTS_ANCHOR} = domainConfig.get('editorial')
    const commentsSection = document.getElementById(ARTICLE_COMMENTS_ANCHOR)
    commentsSection.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest'
    })
  }

  if (shouldRenderLink)
    return (
      <Link className={COMMENTS_CLASS} href={`${NEWS_COMMENTS_BASE_PATH}${slug}`}>
        {content}
      </Link>
    )

  return (
    <span className={COMMENTS_CLASS} onClick={handleClick}>
      {content}
    </span>
  )
}
Comments.propTypes = {
  linkFactory: PropTypes.func,
  slug: PropTypes.string,
  totalNumberOfComments: PropTypes.number
}

PanelNewsDetailHeader.propTypes = {
  actionSlot: PropTypes.node,
  author: PropTypes.object,
  category: PropTypes.string,
  date: PropTypes.string,
  linkFactory: PropTypes.func,
  mainImageSrc: PropTypes.string,
  showNumberOfComments: PropTypes.bool,
  slug: PropTypes.string,
  tag: PropTypes.string,
  title: PropTypes.string,
  totalNumberOfComments: PropTypes.number
}
PanelNewsDetailHeader.displayName = 'PanelNewsDetailHeader'

export default PanelNewsDetailHeader
